import { useState, useCallback } from "react";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";
import { CreateCompanyRequestDTO } from "../../data/models/CompanyDTOs";
import { CompanyRepositoryImpl } from "../../data/repositories/CompanyRepositoryImpl";
import {
  CreateCompanyUseCase,
  GetCompaniesUseCase,
  DeleteCompanyUseCase,
} from "../../domain/use-cases/companies";

// Create repository instance (singleton)
const companyRepository = new CompanyRepositoryImpl();

// Create use cases
const createCompanyUseCase = new CreateCompanyUseCase(companyRepository);
const getCompaniesUseCase = new GetCompaniesUseCase(companyRepository);
const deleteCompanyUseCase = new DeleteCompanyUseCase(companyRepository);

export interface UseCompaniesReturn {
  companies: CompanyEntity[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
  loading: boolean;
  error: Error | null;
  fetchCompanies: (page?: number, limit?: number, search?: string) => Promise<void>;
  createCompany: (data: CreateCompanyRequestDTO) => Promise<{
    company: CompanyEntity;
    admin_credentials: { email: string; password: string };
  }>;
  deleteCompany: (companyId: number) => Promise<void>;
}

export const useCompanies = (): UseCompaniesReturn => {
  const [companies, setCompanies] = useState<CompanyEntity[]>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0,
    total_pages: 1,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCompanies = useCallback(
    async (page: number = 1, limit: number = 50, search?: string) => {
      try {
        setLoading(true);
        setError(null);
        const result = await getCompaniesUseCase.execute(page, limit, search);
        setCompanies(result.companies);
        setPagination(result.pagination);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to fetch companies")
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createCompany = useCallback(
    async (data: CreateCompanyRequestDTO) => {
      try {
        setError(null);
        const result = await createCompanyUseCase.execute(data);
        // Refresh companies list
        await fetchCompanies(pagination.current_page, pagination.per_page);
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to create company")
        );
        throw err;
      }
    },
    [fetchCompanies, pagination]
  );

  const deleteCompany = useCallback(
    async (companyId: number) => {
      try {
        setError(null);
        await deleteCompanyUseCase.execute(companyId);
        // Refresh companies list
        await fetchCompanies(pagination.current_page, pagination.per_page);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to delete company")
        );
        throw err;
      }
    },
    [fetchCompanies, pagination]
  );

  return {
    companies,
    pagination,
    loading,
    error,
    fetchCompanies,
    createCompany,
    deleteCompany,
  };
};

